/**
 * GeneralSchoolSection — display name / website / timezone (Prompt 11F.8).
 *
 * Deliberately narrow: contact fields (email/phone/address) live on the
 * owner-only Organisation & Tax section instead (support_email/
 * support_phone/registered_address) so there is exactly one editable copy
 * of each fact — see `backend/app/features/settings/config/rules.py`'s
 * `SCHOOL_DEFAULTS` docstring.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, Button, Input, Dropdown } from '@/components/common';
import { useTenant } from '@core/tenant/TenantContext';
import { useSchoolConfig, useUpdateSchoolConfig, type SchoolConfig } from '@features/settings/api/config';
import type { ApiError } from '@core/api/client';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
];

interface FormValues {
  school_name: string;
  website: string;
  timezone: string;
}

function toFormValues(cfg: SchoolConfig | undefined): FormValues {
  return {
    school_name: cfg?.school_name ?? '',
    website: cfg?.website ?? '',
    timezone: cfg?.timezone ?? 'Asia/Kolkata',
  };
}

export const GeneralSchoolSection: React.FC = () => {
  const { isAllMode, selectedTenantId } = useTenant();
  const { data: config, isLoading } = useSchoolConfig();
  const updateConfig = useUpdateSchoolConfig();

  const { register, handleSubmit, reset, setValue, watch, formState } = useForm<FormValues>({
    defaultValues: toFormValues(config),
  });

  useEffect(() => {
    if (config) reset(toFormValues(config));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = await updateConfig.mutateAsync(values);
      reset(toFormValues(saved));
      toast.success('School information updated.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update school information.');
    }
  });

  return (
    <Card padding="lg" data-testid="candeza-settings-section-general">
      <h3 style={{ marginTop: 0 }}>School Information — {selectedTenantId}</h3>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
        Display name, website, and timezone for this school. Legal identity and contact details
        live under Organisation &amp; Tax.
      </p>

      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-general-all-mode-banner">
          Select a school to edit School Information.
        </ReadOnlyBanner>
      )}

      <form onSubmit={onSubmit} noValidate>
        <FormGrid>
          <Input
            label="School Name"
            disabled={isAllMode || isLoading}
            {...register('school_name')}
            data-testid="candeza-school-name"
          />
          <Input
            label="Website"
            disabled={isAllMode || isLoading}
            {...register('website')}
            data-testid="candeza-school-website"
          />
          <Dropdown
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={watch('timezone')}
            onChange={(v) => setValue('timezone', v, { shouldDirty: true })}
          />
        </FormGrid>
        <FormActions>
          <Button
            type="submit"
            disabled={isAllMode || formState.isSubmitting || !formState.isDirty}
            data-testid="candeza-settings-save-general"
          >
            {formState.isSubmitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </FormActions>
      </form>
    </Card>
  );
};

export default GeneralSchoolSection;
